import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Plus, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SERVICE_LONG, PAY_STATUS, PAY_METHOD, fmtDateTime, filsToInput, todayISO, fmtChanges } from "@/lib/util";
import { Loader, Pill, Money, Row, Section, Field, VehNo, payColor } from "@/components/common";
import Modal from "@/components/Modal";
import PdfActions from "@/components/PdfActions";

const CORR = [["price", "السعر (د.ك)"], ["customer_name", "اسم العميل"], ["requester_phone", "الهاتف"], ["car_plate", "لوحة السيارة"], ["load_desc", "وصف الحمولة"], ["pickup", "موقع الاستلام"], ["dropoff", "موقع التسليم"]];

export default function InvoiceDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const [inv, setInv] = useState(null);
  const [m, setM] = useState("");
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch((e) => { toast.error(errMsg(e)); setInv(false); }), [id]);
  useEffect(() => { load(); }, [load]);
  if (inv === null) return <Loader />;
  if (!inv) return <p>الفاتورة غير موجودة</p>;
  const remaining = inv.total_fils - inv.paid_fils;

  const act = async (fn, ok) => {
    setBusy(true);
    try { setInv((await fn()).data); toast.success(ok); setM(""); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const openCorr = () => { setF({ reason: "", ...Object.fromEntries(CORR.map(([k]) => [k, k === "price" ? filsToInput(inv.total_fils) : inv[k] || ""])) }); setM("correct"); };
  const submitCorr = () => {
    const body = { reason: f.reason };
    CORR.forEach(([k]) => { const orig = k === "price" ? filsToInput(inv.total_fils) : inv[k] || ""; if (f[k] !== orig) body[k] = f[k]; });
    act(() => api.post(`/invoices/${id}/correct`, body), "تم تسجيل التصحيح");
  };

  return (
    <div data-testid="invoice-detail">
      <Link to="/admin/invoices" className="btn btn-sm btn-ghost mb-4"><ArrowRight className="w-4 h-4" />الفواتير</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-3xl font-black">فاتورة <span className="ltr">{inv.number}</span></h1>
        <div className="flex gap-2">
          {can("payments_record") && remaining > 0 && <button data-testid="add-invoice-payment-btn" onClick={() => { setF({ amount: filsToInput(remaining), method: "cash", note: "", at: todayISO() }); setM("pay"); }} className="btn btn-md btn-orange"><Plus className="w-5 h-5" />تسجيل دفعة</button>}
          {can("invoices_manage") && <button data-testid="correct-invoice-btn" onClick={openCorr} className="btn btn-md btn-ghost"><PencilLine className="w-5 h-5" />تصحيح الفاتورة</button>}
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          <Section title="بيانات الفاتورة">
            <Row label="تاريخ الإصدار">{fmtDateTime(inv.issued_at)}</Row>
            <Row label="رقم النقلة"><Link className="ltr text-blue-700" to={`/admin/trips/${inv.trip_id}`}>{inv.trip_number}</Link></Row>
            <Row label="الخدمة">{SERVICE_LONG[inv.service_type]}</Row>
            <Row label="العميل">{inv.customer_name || "—"}</Row>
            <Row label="الهاتف"><span className="ltr">{inv.requester_phone}</span></Row>
            {inv.service_type === "tow" ? <Row label="لوحة السيارة المنقولة"><span className="ltr">{inv.car_plate}</span></Row> : <Row label="الحمولة">{inv.goods_type} {inv.load_desc}</Row>}
            <Row label="الاستلام">{inv.pickup}</Row>
            <Row label="التسليم النهائي">{inv.dropoff}</Row>
            <Row label="السائق">{inv.driver_name} <span className="ltr">{inv.driver_serial}</span></Row>
            <Row label="شاحنة النقل"><VehNo adm={inv.vehicle_admin_number} plate={inv.vehicle_plate} /></Row>
          </Section>
          <Section title="المبالغ">
            <Row label="سعر النقل"><Money fils={inv.total_fils} testid="inv-total" /></Row>
            <Row label="المحصل"><Money fils={inv.paid_fils} testid="inv-paid" className="text-green-700" /></Row>
            <Row label="المتبقي"><Money fils={remaining} testid="inv-remaining" className="text-red-600" /></Row>
            <Row label="طريقة الدفع">{PAY_METHOD[inv.payment_method] || "—"}</Row>
            <Row label="حالة الدفع"><Pill color={payColor(inv.payment_status)} testid="inv-status">{PAY_STATUS[inv.payment_status]}</Pill></Row>
          </Section>
          <Section title="ملفات PDF">
            <div className="space-y-3">
              <div className="font-bold text-slate-600">نسخة العميل</div>
              <PdfActions path={`/invoices/${id}/pdf?copy=customer`} filename={`${inv.number}.pdf`} testid="inv-customer-pdf" />
              <div className="font-bold text-slate-600">النسخة الداخلية (نفس الفاتورة المالية)</div>
              <PdfActions path={`/invoices/${id}/pdf?copy=internal`} filename={`${inv.number}-internal.pdf`} label="النسخة الداخلية" testid="inv-internal-pdf" />
            </div>
          </Section>
        </div>
        <div className="space-y-5">
          <Section title="سجل التحصيل">
            {inv.payments.length === 0 && <p className="text-slate-500">لا توجد دفعات</p>}
            <div className="space-y-2" data-testid="invoice-payments">
              {inv.payments.map((p) => (
                <div key={p.id} className={`rounded-xl p-3 border ${p.reversal_of ? "bg-red-50" : p.reversed ? "bg-slate-50 opacity-60" : p.confirmed ? "bg-green-50" : "bg-amber-50"}`}>
                  <div className="flex justify-between gap-2"><Money fils={p.amount_fils} /><span>{PAY_METHOD[p.method]}</span></div>
                  <div className="text-sm text-slate-600">{fmtDateTime(p.at)} • {p.by_name} {p.note && `• ${p.note}`}</div>
                  <div className="flex gap-2 mt-1 items-center">
                    {p.reversal_of ? <Pill color="red">قيد عكسي</Pill> : p.reversed ? <Pill>معكوسة</Pill> : p.confirmed ? <Pill color="green">مؤكد التحصيل</Pill> : <Pill color="amber">بانتظار تأكيد التحصيل</Pill>}
                    {can("payments_record") && !p.reversal_of && !p.reversed && !p.confirmed && <button data-testid={`confirm-pay-${p.id}`} onClick={() => act(() => api.post(`/invoices/${id}/payments/${p.id}/confirm`), "تم تأكيد التحصيل")} className="btn btn-sm btn-green">تأكيد التحصيل</button>}
                    {can("payments_record") && !p.reversal_of && !p.reversed && <button data-testid={`reverse-pay-${p.id}`} onClick={() => { setF({ reason: "", pid: p.id }); setM("reverse"); }} className="btn btn-sm btn-ghost text-red-600">عكس</button>}
                  </div>
                </div>
              ))}
            </div>
          </Section>
          <Section title="سجل التصحيحات">
            {inv.corrections.length === 0 ? <p className="text-slate-500">لا توجد تصحيحات. الأصل محفوظ دائماً.</p> : inv.corrections.map((c) => (
              <div key={c.id} className="border-b py-2 text-sm" data-testid="invoice-correction">
                <b>{c.reason}</b> — {c.by_name} • {fmtDateTime(c.at)}
                <div className="text-slate-600">قبل: {fmtChanges(c.before)}</div>
                <div className="text-slate-900">بعد: {fmtChanges(c.after)}</div>
              </div>))}
          </Section>
        </div>
      </div>
      <Modal open={m === "pay"} onClose={() => setM("")} title="تسجيل دفعة على الفاتورة" testid="invoice-pay-modal">
        <Field label="المبلغ (د.ك)"><input data-testid="ip-amount" className="field ltr text-left" inputMode="decimal" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
        <Field label="طريقة الدفع"><select data-testid="ip-method" className="field" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>{Object.entries(PAY_METHOD).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        {f.method === "link" && <p className="text-sm text-orange-700">دفعات «رابط دفع» تُسجل بانتظار التأكيد ولا تُحتسب حتى تأكيد التحصيل.</p>}
        <Field label="ملاحظة"><input className="field" value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <button data-testid="ip-save" disabled={busy} onClick={() => act(() => api.post(`/invoices/${id}/payments`, { amount: f.amount, method: f.method, note: f.note }), "تم تسجيل الدفعة")} className="btn btn-lg btn-orange w-full">حفظ الدفعة</button>
      </Modal>
      <Modal open={m === "reverse"} onClose={() => setM("")} title="عكس دفعة" testid="invoice-reverse-modal">
        <Field label="السبب" required><input data-testid="rev-reason" className="field" value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
        <button data-testid="rev-save" disabled={busy} onClick={() => act(() => api.post(`/invoices/${id}/payments/${f.pid}/reverse`, { reason: f.reason }), "تم عكس الدفعة")} className="btn btn-lg btn-red w-full">تأكيد العكس</button>
      </Modal>
      <Modal open={m === "correct"} onClose={() => setM("")} title="تصحيح الفاتورة" testid="invoice-correct-modal">
        <p className="text-sm text-slate-500">يُحفظ الأصل وسبب التصحيح في السجل. عدّل الحقول المطلوبة فقط.</p>
        {CORR.map(([k, l]) => <Field key={k} label={l}><input data-testid={`corr-${k}`} className="field field-sm" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>)}
        <Field label="سبب التصحيح" required><input data-testid="corr-reason" className="field" value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
        <button data-testid="corr-save" disabled={busy} onClick={submitCorr} className="btn btn-lg btn-dark w-full">حفظ التصحيح</button>
      </Modal>
    </div>
  );
}
